// src/pages/LoanManagement.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { apiUrl } from '../config/api';

// URL for the loan management backend endpoint.
const API_URL = apiUrl('loan_management.php');
// URL to fetch the list of staff for the Attended Staff dropdown.
const STAFF_API_URL = apiUrl('get_staff.php');

// Options for payment method and payment status
const paymentMethodOptions = [
  { label: "Cash", value: "cash" },
  { label: "Card", value: "card" },
  { label: "Online", value: "online" },
];

const paymentStatusOptions = [
  { label: "Paid", value: "paid" },
  { label: "Pending", value: "pending" },
  { label: "Cancelled", value: "cancelled" },
];

const LoanManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State for loan records.
  const [loans, setLoans] = useState([]);
  // Modal form open flag.
  const [openModal, setOpenModal] = useState(false);
  // Edit mode flag.
  const [isEditMode, setIsEditMode] = useState(false);
  // Loan form data.
  const [loanData, setLoanData] = useState({
    id: null,
    attended_staff: '', // Stores the selected staff id
    loan_amount: '',
    repay_amount: '',
    monthly_installment: '',
    total_installments: '',
    paid_installments: '',
    balance_installments: '',
    settled_installments: '', // Number of installments recorded as settled
    comment: '',
  });
  // Staff list for the dropdown.
  const [staffList, setStaffList] = useState([]);

  // Fetch all loan records.
  const fetchLoans = () => {
    fetch(API_URL)
      .then((res) => res.json())
      .then((data) => {
        setLoans(data);
      })
      .catch((error) => console.error('Error fetching loans:', error));
  };

  // Fetch the staff list from the staff endpoint.
  const fetchStaffList = () => {
    fetch(STAFF_API_URL)
      .then((res) => res.json())
      .then((data) => {
        setStaffList(data);
      })
      .catch((error) => console.error('Error fetching staff list:', error));
  };

  useEffect(() => {
    fetchLoans();
    fetchStaffList();
  }, []);

  // Open the modal for adding or editing a loan.
  const handleOpenModal = (loan = null) => {
    if (loan) {
      setLoanData(loan);
      setIsEditMode(true);
    } else {
      setLoanData({
        id: null,
        attended_staff: '',
        loan_amount: '',
        repay_amount: '',
        monthly_installment: '',
        total_installments: '',
        paid_installments: '',
        balance_installments: '',
        settled_installments: '',
        comment: '',
      });
      setIsEditMode(false);
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setLoanData({
      id: null,
      attended_staff: '',
      loan_amount: '',
      repay_amount: '',
      monthly_installment: '',
      total_installments: '',
      paid_installments: '',
      balance_installments: '',
      settled_installments: '',
      comment: '',
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLoanData({ ...loanData, [name]: value });
  };

  // Save (add/update) loan record.
  const handleSave = () => {
    const action = isEditMode ? 'update' : 'add';
    fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, ...loanData }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 'success') {
          fetchLoans();
          handleCloseModal();
        } else {
          console.error('Error saving loan:', data.message);
        }
      })
      .catch((error) => console.error('Error:', error));
  };

  // Delete a loan record.
  const handleDelete = (id) => {
    if (!window.confirm('Are you sure you want to delete this loan record?')) return;
    fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'delete', id }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 'success') {
          fetchLoans();
        } else {
          console.error('Error deleting loan:', data.message);
        }
      })
      .catch((error) => console.error('Error:', error));
  };

  // Helper: Lookup staff name using staffList by staff id.
  const getStaffName = (staffId) => {
    const staff = staffList.find((s) => s.id === staffId);
    return staff ? staff.name : staffId;
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Loan Management
      </Typography>
      <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModal()}>
        Record New Loan
      </Button>
      <IconButton onClick={fetchLoans} color="inherit" sx={{ ml: 1 }}>
        <RefreshIcon />
      </IconButton>

      {isMobile ? (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {loans.length > 0 ? (
            loans.map((loan) => (
              <Card key={loan.id} variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Loan Amount: {loan.loan_amount}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Repay Amount:</strong> {loan.repay_amount}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Monthly Installment:</strong> {loan.monthly_installment}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Installments:</strong> {loan.total_installments} (Paid: {loan.paid_installments}, Balance: {loan.balance_installments})
                  </Typography>
                  <Typography variant="body2">
                    <strong>Settled Installments:</strong> {loan.settled_installments}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Staff:</strong> {getStaffName(loan.attended_staff)}
                  </Typography>
                  {loan.comment && (
                    <Typography variant="body2">
                      <strong>Comment:</strong> {loan.comment}
                    </Typography>
                  )}
                </CardContent>
                <CardActions>
                  <IconButton onClick={() => handleOpenModal(loan)} color="primary">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(loan.id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            ))
          ) : (
            <Typography align="center" variant="body1">
              No loan records found.
            </Typography>
          )}
        </Box>
      ) : (
        <Paper sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Staff</TableCell>
                <TableCell>Loan Amount</TableCell>
                <TableCell>Repay Amount</TableCell>
                <TableCell>Monthly Installment</TableCell>
                <TableCell>Total Installments</TableCell>
                <TableCell>Paid Installments</TableCell>
                <TableCell>Balance Installments</TableCell>
                <TableCell>Settled Installments</TableCell>
                <TableCell>Comment</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loans.map((loan) => (
                <TableRow key={loan.id}>
                  <TableCell>{getStaffName(loan.attended_staff)}</TableCell>
                  <TableCell>{loan.loan_amount}</TableCell>
                  <TableCell>{loan.repay_amount}</TableCell>
                  <TableCell>{loan.monthly_installment}</TableCell>
                  <TableCell>{loan.total_installments}</TableCell>
                  <TableCell>{loan.paid_installments}</TableCell>
                  <TableCell>{loan.balance_installments}</TableCell>
                  <TableCell>{loan.settled_installments}</TableCell>
                  <TableCell>{loan.comment}</TableCell>
                  <TableCell align="center">
                    <IconButton onClick={() => handleOpenModal(loan)} color="primary">
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(loan.id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loans.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    No loan records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Modal for Adding/Editing a Loan */}
      <Dialog open={openModal} onClose={handleCloseModal} fullWidth>
        <DialogTitle>{isEditMode ? "Edit Loan" : "Record New Loan"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="loan_amount"
            label="Loan Amount"
            type="number"
            fullWidth
            variant="standard"
            value={loanData.loan_amount}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="repay_amount"
            label="Repay Amount"
            type="number"
            fullWidth
            variant="standard"
            value={loanData.repay_amount}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="monthly_installment"
            label="Monthly Installment"
            type="number"
            fullWidth
            variant="standard"
            value={loanData.monthly_installment}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="total_installments"
            label="Total Installments"
            type="number"
            fullWidth
            variant="standard"
            value={loanData.total_installments}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="paid_installments"
            label="Paid Installments"
            type="number"
            fullWidth
            variant="standard"
            value={loanData.paid_installments}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="balance_installments"
            label="Balance Installments"
            type="number"
            fullWidth
            variant="standard"
            value={loanData.balance_installments}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="settled_installments"
            label="Settled Installments"
            type="number"
            fullWidth
            variant="standard"
            value={loanData.settled_installments}
            onChange={handleInputChange}
          />
          {/* Autocomplete for Attended Staff */}
          <Autocomplete
            options={staffList}
            getOptionLabel={(option) => `${option.name} (${option.id})`}
            value={
              loanData.attended_staff
                ? staffList.find((staff) => staff.id === loanData.attended_staff) || null
                : null
            }
            onChange={(event, newValue) => {
              setLoanData({ ...loanData, attended_staff: newValue ? newValue.id : '' });
            }}
            renderInput={(params) => (
              <TextField {...params} label="Attended Staff" margin="dense" variant="standard" fullWidth />
            )}
            sx={{ mt: 1, mb: 1 }}
          />
          <TextField
            margin="dense"
            name="comment"
            label="Comment"
            fullWidth
            multiline
            rows={2}
            variant="standard"
            value={loanData.comment}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoanManagement;
